'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AuctionItem } from '@/app/components/AuctionDetailModal';
import { interestsApi } from '@/lib/api';
import { beAuctionToAuctionItem } from '@/lib/api/adapters';
import { useAuth } from './AuthContext';

type InterestsContextValue = {
  items: AuctionItem[];
  count: number;
  isSynced: boolean;
  refresh: () => Promise<void>;
  isInterest: (auctionId: string) => boolean;
  addInterest: (auction: AuctionItem) => Promise<void>;
  removeInterest: (auctionId: string) => Promise<void>;
  setInterestExclusion: (auctionId: string, marked: boolean) => Promise<void>;
};

const InterestsContext = createContext<InterestsContextValue | null>(null);

export function InterestsProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isOnline } = useAuth();
  const [items, setItems] = useState<AuctionItem[]>([]);
  const [isSynced, setIsSynced] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !isOnline) return;
    try {
      const rows = await interestsApi.list();
      setItems(
        rows.map((r) =>
          beAuctionToAuctionItem(r.auction, {
            interestMarkedForExclusion: r.markedForExclusion,
          })
        )
      );
      setIsSynced(true);
    } catch {
      setIsSynced(false);
    }
  }, [isAuthenticated, isOnline]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!isAuthenticated) {
      setItems([]);
      setIsSynced(false);
    }
  }, [isAuthenticated]);

  const isInterest = useCallback(
    (auctionId: string) =>
      items.some((a) => {
        const bid = (a as AuctionItem & { beId?: string }).beId;
        return bid === auctionId || a.id === auctionId;
      }),
    [items]
  );

  const addInterest = useCallback(async (auction: AuctionItem) => {
    const beId = (auction as AuctionItem & { beId?: string }).beId;
    if (!beId) return;
    const row = await interestsApi.add(beId);
    const item = beAuctionToAuctionItem(row.auction, {
      interestMarkedForExclusion: row.markedForExclusion,
    });
    setItems((prev) => {
      const exists = prev.some((a) => {
        const bid = (a as AuctionItem & { beId?: string }).beId;
        return bid === beId;
      });
      if (exists) {
        return prev.map((a) => {
          const bid = (a as AuctionItem & { beId?: string }).beId;
          return bid === beId ? item : a;
        });
      }
      return [item, ...prev];
    });
  }, []);

  const removeInterest = useCallback(async (auctionId: string) => {
    await interestsApi.remove(auctionId);
    setItems((prev) =>
      prev.filter((a) => {
        const bid = (a as AuctionItem & { beId?: string }).beId;
        return bid !== auctionId;
      })
    );
  }, []);

  const setInterestExclusion = useCallback(async (auctionId: string, marked: boolean) => {
    const updated = await interestsApi.updateExclusion(auctionId, marked);
    setItems((prev) =>
      prev.map((a) => {
        const bid = (a as AuctionItem & { beId?: string }).beId;
        if (bid !== auctionId) return a;
        return beAuctionToAuctionItem(updated.auction, {
          interestMarkedForExclusion: updated.markedForExclusion,
        });
      })
    );
  }, []);

  const value = useMemo<InterestsContextValue>(
    () => ({
      items,
      count: items.length,
      isSynced,
      refresh,
      isInterest,
      addInterest,
      removeInterest,
      setInterestExclusion,
    }),
    [items, isSynced, refresh, isInterest, addInterest, removeInterest, setInterestExclusion]
  );

  return <InterestsContext.Provider value={value}>{children}</InterestsContext.Provider>;
}

export function useInterests() {
  const ctx = useContext(InterestsContext);
  if (!ctx) throw new Error('useInterests must be used within InterestsProvider');
  return ctx;
}
